import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { todayISO, shiftISO } from "@/lib/calendar";
import { friendlyError } from "@/lib/errors";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import useMediaQuery from "@/hooks/useMediaQuery";
import { CalendarDays, Loader2, Minus, Plus } from "lucide-react";
import { toast } from "sonner";

const DURATIONS = [7, 14, 30];

// Guided post-avatar flow (Epic 15.2): right after the first avatar is
// confirmed, offer to spin up the editorial calendar automatically. Confirming
// POSTs /avatars/{id}/guided-calendar and drops the user inside the
// calendario-editorial agent with the kickoff message pre-sent (15.4).
export default function GuidedCalendarSheet({ open, onOpenChange, avatarId }) {
  const nav = useNavigate();
  const [startDate, setStartDate] = useState(() => shiftISO(todayISO(), 1, "day"));
  const [reels, setReels] = useState(3);
  const [carrossel, setCarrossel] = useState(1);
  const [stories, setStories] = useState(3);
  const [duration, setDuration] = useState(14);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // >=md (Tailwind md breakpoint): centered modal dialog; <md: bottom sheet.
  const isDesktop = useMediaQuery("(min-width: 768px)");

  const confirm = async () => {
    if (!startDate || saving) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/avatars/${avatarId}/guided-calendar`, {
        start_date: startDate,
        reels_per_week: reels,
        carrossel_per_week: carrossel,
        stories_per_week: stories,
        duration_days: duration,
      });
      nav(`/agent/calendario-editorial?thread=${data.thread_id}&autosend=${encodeURIComponent(data.message)}`);
    } catch (err) {
      toast.error(friendlyError(err));
      setSaving(false);
    }
  };

  const formBody = (
    <>
      {expanded ? (
        <>
          <div className="mt-5">
            <label className="block text-[11px] tracking-[0.2em] uppercase text-brand-ink2 font-ui mb-2">
              A partir de quando?
            </label>
            <input
              data-testid="guided-cal-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full bg-brand-elevated border border-brand-line rounded-2xl px-4 py-3 text-[16px] text-brand-ink focus:outline-none focus:border-brand-rose transition-colors min-h-[48px]"
            />
          </div>

          <div className="mt-5 space-y-3">
            <Stepper label="Reels por semana" value={reels} onChange={setReels} testid="guided-cal-reels" />
            <Stepper label="Carrosséis por semana" value={carrossel} onChange={setCarrossel} testid="guided-cal-carrossel" />
            <Stepper label="Stories por semana" value={stories} onChange={setStories} testid="guided-cal-stories" />
          </div>

          <div className="mt-5">
            <label className="block text-[11px] tracking-[0.2em] uppercase text-brand-ink2 font-ui mb-2">
              Duração do calendário
            </label>
            <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Duração">
              {DURATIONS.map((d) => (
                <button
                  key={d}
                  type="button"
                  role="radio"
                  aria-checked={duration === d}
                  data-testid={`guided-cal-duration-${d}`}
                  onClick={() => setDuration(d)}
                  className={`inline-flex items-center justify-center px-4 py-3 rounded-2xl text-[13.5px] font-ui transition-colors min-h-[48px] border ${
                    duration === d
                      ? "bg-brand-rose text-white border-brand-rose"
                      : "bg-brand-elevated text-brand-ink2 border-brand-line hover:border-brand-rose/60"
                  }`}
                >
                  {d} dias
                </button>
              ))}
            </div>
          </div>
        </>
      ) : (
        <p
          data-testid="guided-cal-summary"
          className="mt-5 text-[13.5px] text-brand-ink font-ui bg-brand-elevated border border-brand-line rounded-2xl px-4 py-3"
        >
          14 dias a partir de amanhã · 3 Reels + 1 Carrossel + 3 Stories por semana
        </p>
      )}

      <button
        type="button"
        data-testid="guided-cal-confirm"
        onClick={confirm}
        disabled={!startDate || saving}
        className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-brand-rose hover:bg-brand-roseHover text-white px-6 py-3.5 rounded-full text-[14px] font-ui transition-colors min-h-[48px] disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarDays className="w-4 h-4" />}
        Gerar meu calendário ✦
      </button>
      {!expanded && (
        <button
          type="button"
          data-testid="guided-cal-adjust"
          onClick={() => setExpanded(true)}
          disabled={saving}
          className="mt-2 w-full inline-flex items-center justify-center px-6 py-2 text-[13px] font-ui text-brand-ink2 hover:text-brand-rose underline underline-offset-4 transition-colors disabled:opacity-50"
        >
          ajustar ritmo
        </button>
      )}
      <button
        type="button"
        data-testid="guided-cal-skip"
        onClick={() => nav("/avatars")}
        disabled={saving}
        className="mt-2 w-full inline-flex items-center justify-center px-6 py-3 rounded-full border border-brand-line text-brand-ink2 hover:border-brand-rose hover:text-brand-ink text-[13.5px] font-ui transition-colors min-h-[44px] disabled:opacity-50"
      >
        Agora não
      </button>
    </>
  );

  const titleNode = (
    <>
      <CalendarDays className="w-5 h-5 text-brand-rose" />
      Avatar validado! Vamos criar seu calendário de conteúdo?
    </>
  );
  const descriptionText = "Bate com o cliente que você mais vende? Então me diz o ritmo que a gente monta sua agenda de conteúdo.";

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          data-testid="guided-calendar-sheet"
          className="bg-brand-surface border-brand-line max-w-[520px] max-h-[88vh] overflow-y-auto overflow-x-hidden scrollbar-soft"
        >
          <DialogHeader className="text-left">
            <DialogTitle className="font-display text-2xl text-brand-ink flex items-center gap-2">
              {titleNode}
            </DialogTitle>
            <DialogDescription className="text-brand-ink2">
              {descriptionText}
            </DialogDescription>
          </DialogHeader>
          {formBody}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        data-testid="guided-calendar-sheet"
        className="bg-brand-surface border-brand-line rounded-t-3xl max-h-[88vh] overflow-y-auto overflow-x-hidden scrollbar-soft"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-2xl text-brand-ink flex items-center gap-2">
            {titleNode}
          </SheetTitle>
          <SheetDescription className="text-brand-ink2">
            {descriptionText}
          </SheetDescription>
        </SheetHeader>
        {formBody}
      </SheetContent>
    </Sheet>
  );
}

function Stepper({ label, value, onChange, min = 0, max = 7, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 bg-brand-elevated border border-brand-line rounded-2xl px-4 py-2.5" data-testid={testid}>
      <span className="text-[13.5px] text-brand-ink font-ui">{label}</span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid={`${testid}-minus`}
          onClick={() => onChange(Math.max(min, value - 1))}
          disabled={value <= min}
          aria-label={`Diminuir ${label}`}
          className="w-9 h-9 flex items-center justify-center rounded-full border border-brand-line text-brand-ink2 hover:border-brand-rose hover:text-brand-rose transition-colors disabled:opacity-40"
        >
          <Minus className="w-4 h-4" />
        </button>
        <span className="w-6 text-center font-display text-lg text-brand-ink tabular-nums" data-testid={`${testid}-value`}>
          {value}
        </span>
        <button
          type="button"
          data-testid={`${testid}-plus`}
          onClick={() => onChange(Math.min(max, value + 1))}
          disabled={value >= max}
          aria-label={`Aumentar ${label}`}
          className="w-9 h-9 flex items-center justify-center rounded-full border border-brand-line text-brand-ink2 hover:border-brand-rose hover:text-brand-rose transition-colors disabled:opacity-40"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
